import { useEffect, useRef, useState } from "react";
import { listen } from "@tauri-apps/api/event";
import {
  COMPACT_RESOURCE_BROWSER_EVENT,
  COMPACT_RESOURCE_LIFECYCLE_EVENT,
  type CompactResourceLifecycle,
} from "./compact-resource-events";
import { isTauri } from "@/lib/is-tauri";
import { safeTauriUnlisten } from "./tauri-listener-cleanup";
import { FieldEditCueMark, type FieldEditCue } from "./field-edit-cue-mark";
import { useWindowOrigin } from "./use-window-origin";

function fieldCue(event: CompactResourceLifecycle, id: number): FieldEditCue | null {
  const bounds = event.bounds;
  if (event.phase !== "field_glow" || event.showCue !== true || !bounds
    || !event.fieldName || ![bounds.x, bounds.y, bounds.width, bounds.height]
      .every((value) => Number.isFinite(value))
    || bounds.width <= 0 || bounds.height <= 0
    || (event.editStatus !== "verified" && event.editStatus !== "uncertain")) return null;
  return { id, name: event.fieldName, status: event.editStatus, bounds };
}

/** Visual-only feedback. The native action never waits for this animation. */
export function DesktopFieldEditCues() {
  const origin = useWindowOrigin();
  const [cues, setCues] = useState<FieldEditCue[]>([]);
  const sequence = useRef(0);
  const latestByExecution = useRef(new Map<string, number>());
  useEffect(() => {
    let cancelled = false;
    const timers = new Set<number>();
    const receive = (event: CompactResourceLifecycle) => {
      if (event.phase !== "field_glow" || !Number.isSafeInteger(event.cueSequence)
        || (event.cueSequence ?? 0) < 1) return;
      const latest = latestByExecution.current.get(event.executionId) ?? 0;
      if (event.cueSequence! <= latest) return;
      const cue = fieldCue(event, ++sequence.current);
      if (!cue || cancelled) return;
      latestByExecution.current.set(event.executionId, event.cueSequence!);
      if (latestByExecution.current.size > 64) {
        const oldest = latestByExecution.current.keys().next().value;
        if (oldest) latestByExecution.current.delete(oldest);
      }
      setCues((current) => [...current, cue].slice(-8));
      const timer = window.setTimeout(() => {
        timers.delete(timer);
        setCues((current) => current.filter((item) => item.id !== cue.id));
      }, 900);
      timers.add(timer);
    };
    if (!isTauri()) {
      const browserListener = (event: Event) => receive(
        (event as CustomEvent<CompactResourceLifecycle>).detail,
      );
      window.addEventListener(COMPACT_RESOURCE_BROWSER_EVENT, browserListener);
      return () => {
        cancelled = true;
        window.removeEventListener(COMPACT_RESOURCE_BROWSER_EVENT, browserListener);
        timers.forEach(window.clearTimeout);
      };
    }
    let unlisten: (() => void) | null = null;
    void listen<CompactResourceLifecycle>(COMPACT_RESOURCE_LIFECYCLE_EVENT, (event) => {
      receive(event.payload);
    }).then((stop) => {
      if (cancelled) safeTauriUnlisten(stop);
      else unlisten = stop;
    });
    return () => {
      cancelled = true;
      if (unlisten) safeTauriUnlisten(unlisten);
      timers.forEach(window.clearTimeout);
    };
  }, []);
  if (!origin.ready) return null;
  return <div className="pointer-events-none fixed inset-0 z-[9999]" aria-hidden="true" data-testid="desktop-field-edit-cues">
    {cues.map((cue) => {
      const left = cue.bounds.x - origin.x;
      const top = cue.bounds.y - origin.y;
      if (left + cue.bounds.width < 0 || top + cue.bounds.height < 0
        || left > window.innerWidth || top > window.innerHeight) return null;
      return <FieldEditCueMark key={cue.id} cue={cue} left={left} top={top} />;
    })}
  </div>;
}
